/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import classnames from 'classnames';
import { ErrorBoundary } from '../../utilities/global-error-boundary';
import { ICipCtaLinkData } from './cip-cta-link.data';
import { ICipCtaLinkProps } from './cip-cta-link.props.autogenerated';

export interface ICipCtaLinkViewProps extends ICipCtaLinkProps<ICipCtaLinkData> {
    classNames: string;
    ariaLabel: string | undefined;
    target: string | undefined;
    text: string | undefined;
    href: string | undefined;
}

/**
 *
 * CipCtaLink component
 * @extends {React.PureComponent<ICipCtaLinkProps<ICipCtaLinkData>>}
 */
class CipCtaLink extends React.PureComponent<ICipCtaLinkViewProps> {
    private readonly getCipCtaLinkProps = (): ICipCtaLinkViewProps => {
        const {
            config: { link, appearance, className, iconBefore, iconAfter }
        } = this.props;
        return {
            ...this.props,
            classNames: classnames(
                'cip-cta-link',
                'ctaPrimary' === appearance && 'msc-cta__primary',
                'ctaSecondary' === appearance && 'msc-cta__secondary',
                iconBefore && `cip-cta-link--icon-before-${iconBefore}`,
                iconAfter && `cip-cta-link--icon-after-${iconAfter}`,
                className
            ),
            ariaLabel: link?.ariaLabel,
            text: link?.linkText,
            target: link?.openInNewTab ? '_blank' : undefined,
            href: link?.linkUrl && link?.linkUrl.destinationUrl ? link?.linkUrl?.destinationUrl : undefined
        };
    };

    public render(): JSX.Element | null {
        return this.props.renderView(this.getCipCtaLinkProps());
    }
}

export default ErrorBoundary(CipCtaLink, 'cip-cta-link');
